import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, Trophy, Building2, Pencil, Trash2, CheckCircle2, XCircle, Plus, Video,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SourceBadge = ({ source }) => (
  source === 'PFAP' ? (
    <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1" data-testid="md-source-pfap">
      <Trophy className="w-3 h-3" /> PFAP
    </Badge>
  ) : (
    <Badge className="bg-sky-100 text-sky-800 border-sky-200 gap-1" data-testid="md-source-external">
      <Building2 className="w-3 h-3" /> External
    </Badge>
  )
);

const VerificationBadge = ({ status }) => {
  const map = {
    Verified: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Rejected: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <Badge className={`${map[status] || 'bg-gray-100 text-gray-700'} text-xs`}
           data-testid={`md-verification-${status}`}>
      {status || 'Unknown'}
    </Badge>
  );
};

/** Phase 4 — Slice C · Match Detail Page. */
const MatchDetailPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'Admin' || user?.role === 'Staff';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [athletes, setAthletes] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addParticipant, setAddParticipant] = useState({
    athlete_id: '', minutes_played: 90, position: '',
    goals_scored: 0, assists: 0, yellow_cards: 0, red_cards: 0,
    rating: '', athlete_notes: '',
  });
  const [editPart, setEditPart] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, ath] = await Promise.all([
        axios.get(`${API}/matches-v3/${matchId}`),
        axios.get(`${API}/athletes`),
      ]);
      setData(detail.data);
      setAthletes(Array.isArray(ath.data) ? ath.data : (ath.data?.athletes || []));
    } catch (e) {
      toast.error('Failed to load match');
    } finally { setLoading(false); }
  }, [matchId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleVerify = async (status) => {
    if (!canEdit) return;
    try {
      await axios.post(`${API}/matches-v3/${matchId}/verify`, { status });
      toast.success(`Match marked ${status}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update verification');
    }
  };

  const handleDeleteMatch = async () => {
    if (!window.confirm('Delete this match and all participants?')) return;
    try {
      await axios.delete(`${API}/matches-v3/${matchId}`);
      toast.success('Match deleted');
      navigate('/matches');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleAddParticipant = async () => {
    if (!addParticipant.athlete_id) { toast.error('Pick an athlete'); return; }
    try {
      await axios.post(`${API}/matches-v3/${matchId}/participants`, {
        ...addParticipant,
        rating: addParticipant.rating ? parseFloat(addParticipant.rating) : null,
        minutes_played: addParticipant.minutes_played ? parseInt(addParticipant.minutes_played, 10) : null,
      });
      toast.success('Participant added');
      setAddOpen(false);
      setAddParticipant({
        athlete_id: '', minutes_played: 90, position: '',
        goals_scored: 0, assists: 0, yellow_cards: 0, red_cards: 0,
        rating: '', athlete_notes: '',
      });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add participant');
    }
  };

  const handleSaveParticipant = async () => {
    if (!editPart) return;
    try {
      await axios.put(`${API}/matches-v3/participants/${editPart.id}`, {
        minutes_played: editPart.minutes_played ? parseInt(editPart.minutes_played, 10) : null,
        position: editPart.position || null,
        goals_scored: editPart.goals_scored ?? 0,
        assists: editPart.assists ?? 0,
        yellow_cards: editPart.yellow_cards ?? 0,
        red_cards: editPart.red_cards ?? 0,
        rating: editPart.rating ? parseFloat(editPart.rating) : null,
        athlete_notes: editPart.athlete_notes || null,
      });
      toast.success('Participant updated');
      setEditPart(null);
      fetchAll();
    } catch (e) {
      toast.error('Failed to update participant');
    }
  };

  const handleRemoveParticipant = async (pid, name) => {
    if (!window.confirm(`Remove ${name} from this match?`)) return;
    try {
      await axios.delete(`${API}/matches-v3/participants/${pid}`);
      fetchAll();
    } catch {
      toast.error('Failed to remove participant');
    }
  };

  const availableForAdd = useMemo(() => {
    const taken = new Set((data?.participants || []).map(p => p.athlete_id));
    return (athletes || []).filter(a => !taken.has(a.id));
  }, [data, athletes]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }
  if (!data?.match) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500" data-testid="match-not-found">
          Match not found.
        </div>
      </Layout>
    );
  }

  const m = data.match;
  const participants = data.participants || [];

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-5" data-testid="match-detail-page">
        <Button variant="ghost" size="sm" onClick={() => navigate('/matches')} data-testid="back-to-matches">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to matches
        </Button>

        {/* Header */}
        <Card data-testid="match-header-card">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <SourceBadge source={m.match_source} />
                  <VerificationBadge status={m.verification_status} />
                </div>
                <CardTitle className="text-2xl" data-testid="match-detail-title">
                  vs {m.opponent}
                  {m.result && (
                    <span className="ml-3 text-base text-gray-500">({m.result})</span>
                  )}
                </CardTitle>
                <div className="text-sm text-gray-500 mt-1">
                  {m.match_date}{m.kickoff_time ? ` · ${m.kickoff_time}` : ''}
                  {' · '}{m.competition}
                  {m.club_name ? ` · ${m.club_name}` : ''}
                  {m.age_group ? ` · ${m.age_group}` : ''}
                  {m.venue ? ` · ${m.venue}` : ''}
                  {m.formation ? ` · Formation: ${m.formation}` : ''}
                </div>
                {m.notes && (
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">
                    {m.notes}
                  </div>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-2">
                  {m.match_source === 'External' && m.verification_status !== 'Verified' && (
                    <Button size="sm" onClick={() => handleVerify('Verified')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="verify-match-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Verify
                    </Button>
                  )}
                  {m.match_source === 'External' && m.verification_status !== 'Rejected' && (
                    <Button size="sm" variant="outline" onClick={() => handleVerify('Rejected')}
                            data-testid="reject-match-btn">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={handleDeleteMatch}
                          data-testid="delete-match-btn">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Participants */}
        <Card data-testid="participants-card">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">Participants ({participants.length})</CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => navigate(`/matches/${matchId}/performance`)}
                        data-testid="open-performance-workspace">
                  <Video className="w-4 h-4 mr-1" /> Individual Match Performance
                </Button>
                {canEdit && (
                  <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-participant-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Player
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {participants.length === 0 ? (
              <div className="py-8 text-center text-gray-500" data-testid="no-participants">
                No participants yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="participants-table">
                  <thead>
                    <tr className="border-b text-left text-xs text-gray-500 uppercase">
                      <th className="py-2 pr-3">Athlete</th>
                      <th className="py-2 pr-3">Pos</th>
                      <th className="py-2 pr-3">Min</th>
                      <th className="py-2 pr-3">G</th>
                      <th className="py-2 pr-3">A</th>
                      <th className="py-2 pr-3">Y</th>
                      <th className="py-2 pr-3">R</th>
                      <th className="py-2 pr-3">Rating</th>
                      {canEdit && <th className="py-2 pr-3"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map(p => (
                      <tr key={p.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/40"
                          data-testid={`participant-row-${p.id}`}>
                        <td className="py-2 pr-3 font-medium">{p.athlete_name || p.athlete_id}</td>
                        <td className="py-2 pr-3">{p.position || '—'}</td>
                        <td className="py-2 pr-3">{p.minutes_played ?? '—'}</td>
                        <td className="py-2 pr-3">{p.goals_scored ?? '—'}</td>
                        <td className="py-2 pr-3">{p.assists ?? '—'}</td>
                        <td className="py-2 pr-3">{p.yellow_cards ?? '—'}</td>
                        <td className="py-2 pr-3">{p.red_cards ?? '—'}</td>
                        <td className="py-2 pr-3">{p.rating ?? '—'}</td>
                        {canEdit && (
                          <td className="py-2 pr-3 flex items-center gap-1">
                            <Button size="sm" variant="ghost"
                                    onClick={() => navigate(`/matches/${matchId}/performance?athlete=${p.athlete_id}`)}
                                    data-testid={`analyse-participant-${p.id}`}>
                              <Video className="w-3.5 h-3.5 text-sky-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditPart({ ...p })}
                                    data-testid={`edit-participant-${p.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost"
                                    onClick={() => handleRemoveParticipant(p.id, p.athlete_name)}
                                    data-testid={`remove-participant-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add participant dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent data-testid="add-participant-dialog">
            <DialogHeader><DialogTitle>Add Participant</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Athlete</Label>
                <SearchableAthleteSelect
                  athletes={availableForAdd}
                  value={addParticipant.athlete_id}
                  onChange={(v) => setAddParticipant(p => ({ ...p, athlete_id: v }))}
                  placeholder="Pick athlete..."
                  data-testid="add-participant-athlete"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['minutes_played', 'goals_scored', 'assists', 'yellow_cards', 'red_cards', 'rating'].map(k => (
                  <div key={k}>
                    <Label className="capitalize">{k.replace('_', ' ')}</Label>
                    <Input
                      type="number"
                      value={addParticipant[k]}
                      onChange={e => setAddParticipant(p => ({ ...p, [k]: e.target.value }))}
                      data-testid={`add-participant-${k}`}
                    />
                  </div>
                ))}
                <div className="col-span-2">
                  <Label>Position</Label>
                  <Input value={addParticipant.position}
                         onChange={e => setAddParticipant(p => ({ ...p, position: e.target.value }))}
                         data-testid="add-participant-position" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2}
                          value={addParticipant.athlete_notes}
                          onChange={e => setAddParticipant(p => ({ ...p, athlete_notes: e.target.value }))}
                          data-testid="add-participant-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={handleAddParticipant} data-testid="submit-add-participant">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit participant dialog */}
        <Dialog open={!!editPart} onOpenChange={(v) => !v && setEditPart(null)}>
          <DialogContent data-testid="edit-participant-dialog">
            <DialogHeader>
              <DialogTitle>Edit {editPart?.athlete_name}</DialogTitle>
            </DialogHeader>
            {editPart && (
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['minutes_played', 'goals_scored', 'assists', 'yellow_cards', 'red_cards', 'rating'].map(k => (
                    <div key={k}>
                      <Label className="capitalize">{k.replace('_', ' ')}</Label>
                      <Input
                        type="number"
                        value={editPart[k] ?? ''}
                        onChange={e => setEditPart(p => ({ ...p, [k]: e.target.value }))}
                        data-testid={`edit-participant-${k}`}
                      />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <Label>Position</Label>
                    <Input value={editPart.position || ''}
                           onChange={e => setEditPart(p => ({ ...p, position: e.target.value }))}
                           data-testid="edit-participant-position" />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea rows={2}
                            value={editPart.athlete_notes || ''}
                            onChange={e => setEditPart(p => ({ ...p, athlete_notes: e.target.value }))}
                            data-testid="edit-participant-notes" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPart(null)}>Cancel</Button>
              <Button onClick={handleSaveParticipant} data-testid="submit-edit-participant">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MatchDetailPage;
